import {
  useInitChatroomMessagesQuery,
  useUpdateLastReadDateMutation,
} from '../../slices/inboxApiSlice';
import { Outlet, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { FaArrowDown, FaFlag } from 'react-icons/fa';
import { AiOutlineSend, AiOutlineStop } from 'react-icons/ai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '../../slices/auth';
import { DisputeStatus, SocketActions, UserRoles } from '../../utils/constants';
import {
  clearChatroom,
  makeSelectChatroomMessages,
  selectActiveChatroom,
  selectActiveChatroomId,
  selectChatrooms,
  setActiveChatroomId,
} from '../../slices/inboxSlice';
import React from 'react';
import { AvatarImage } from '../avatar-image';
import useIntersectionObserver from '../../hooks/use-intersection-observer';
import { CustomError } from '../../utils/custom-types';
import { LuAlertTriangle } from 'react-icons/lu';
import { DragDropFile } from '../drag-drop-file';
import { dateAndTimeFormatter } from '../../utils/dateFormatter';

const ViewChatRoom = () => {
  const params = useParams();
  const user = useSelector(selectUser);
  const activeChatroom = useSelector(selectActiveChatroom);
  const dispatch = useDispatch();
  const elementRef = useRef(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const selectChatroomMessages = useMemo(
    () => makeSelectChatroomMessages(params.chatroomId!),
    [params.chatroomId],
  );
  const chatroomMessages = useSelector(selectChatroomMessages);
  const [tempChat, setTempChat] = useState('');
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [page, setPage] = useState<number>();
  const [fetch, setFetch] = useState(false);
  const [file, setFile] = useState<File[]>([]);
  const [errorMsg, setErrorMsg] = useState('');

  const [updateLastReadDate] = useUpdateLastReadDateMutation();
  const { data, isLoading, isError, error, isSuccess } = useInitChatroomMessagesQuery({
    id: params.chatroomId,
    page,
  });

  const unansweredMatchGameScreenshot = activeChatroom?.MatchGameScreenshot.filter(
    (mgs: any) => mgs.status === DisputeStatus.NotChecked,
  );

  useEffect(() => {
    if (!data?.id) return;
    dispatch(setActiveChatroomId(data.id));
    return () => {
      dispatch(setActiveChatroomId(null));
    };
  }, [data]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isLoading) setFetch(false);
    else timeout = setTimeout(() => setFetch(true), 2000);
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isLoading]);

  const hasIOSupport = !!window.IntersectionObserver;
  const entry = useIntersectionObserver(elementRef, {});
  const isVisible = !!entry?.isIntersecting;
  useEffect(() => {
    if (!isVisible || !fetch || isLoading || !data || !data.meta?.next) return;
    setPage(data.meta.next);
  }, [isVisible, data?.meta, fetch]);
  useEffect(() => {
    const handleScroll = () => {
      if (chatContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
        setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 10);
      }
    };

    if (chatContainerRef.current) {
      chatContainerRef.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (chatContainerRef.current) {
        chatContainerRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    if (isAtBottom && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatroomMessages, isAtBottom]);

  const chatroomIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (data?.id) {
      chatroomIdRef.current = data.id;
      dispatch(
        dispatch({
          type: SocketActions.ConnectChatroom,
          payload: { id: data.id },
        }),
      );
    }
  }, [data?.id]);
  useEffect(() => {
    return () => {
      updateLastReadDate({ chatroomId: chatroomIdRef.current });
    };
  }, []);

  function sendChat(message: any) {
    if (file && file.length > 0) {
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      const allValid = file.every((f) => validImageTypes.includes(f.type));
      if (!allValid) {
        setErrorMsg('Invalid File Type! Only JPG, PNG, GIF, and WEBP are allowed.');
        return;
      }
      setErrorMsg('');
    }
    let payload: any;
    payload = {
      room: data?.id,
      user_id: user?.id,
      user_roles: user?.roles,
      username: user?.username,
      image: user?.image,
      time: new Date().toTimeString().split(' ')?.[0],
      chatroomId: data?.id,
      message,
      attachments: file ? file : [],
    };
    dispatch({ type: SocketActions.SendChatroomMessage, payload });
    setTempChat('');
    setFile([]);
  }

  const handleSubmit = () => {
    if (!tempChat.length && file.length === 0) return;
    sendChat(tempChat);
  };

  if (isError) {
    console.error('Error loading chat room data:', isError);
    return (error as CustomError)?.data?.message ? (
      <p>{(error as CustomError).data.message}</p>
    ) : (
      <p>Error loading chat room data.</p>
    );
  }
  return (
    <>
      <div className='border-2 border-gray-300 rounded-lg flex flex-col h-full'>
        <div className='flex justify-between bg-gray-300 gap-4 p-4'>
          {(unansweredMatchGameScreenshot?.length ?? 0) > 0 &&
            (user.roles !== UserRoles.Admin ? (
              <div
                attr-count={unansweredMatchGameScreenshot?.length}
                className='p-1 px-1 rounded-3xl border-2 border-fen-red text-fen-red notification-show before:!-top-2 before:!-right-2 before:!border-fen-red before:!bg-white before:!text-fen-red'
              >
                <span className='flex items-center gap-2 text-fen-red'>
                  <LuAlertTriangle />
                  Admin Requested
                </span>
              </div>
            ) : (
              <Link
                to='./chat-disputes'
                attr-count={unansweredMatchGameScreenshot?.length}
                className='cursor-pointer p-1 px-1 rounded-3xl border-2 border-fen-red text-fen-red notification-show before:!-top-2 before:!-right-2 before:!border-fen-red before:!bg-white before:!text-fen-red'
              >
                <span className='flex items-center gap-2 text-fen-red'>
                  <LuAlertTriangle />
                  Admin Requested
                </span>
              </Link>
            ))}
          <div className=' text-xl'>{data?.name}</div>
          <div className=''>
            <Link to={`./dispute-chat/`} className='notification-show before:!-top-2'>
              <button className='cursor-pointer flex items-center justify-center p-1 px-1 bg-fen-red text-white rounded-3xl border-2 min-w-[50px] md:min-w-[150px]'>
                <FaFlag className='mr-1' />
                Flag Chat
              </button>
            </Link>
          </div>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div
            className='flex-1 p-2 gap-2 flex flex-col overflow-auto h-[700px] rounded'
            ref={chatContainerRef}
          >
            {chatroomMessages.map((ch: any, i: number) => (
              <React.Fragment key={i}>
                {user.roles === UserRoles.Admin || user.roles === UserRoles.Ogma ? (
                  <div
                    className={`flex items-center gap-2 w-full max-w-full relative ${
                      ch.isMe || ch.user?.id === user.id || ch?.user_id === user.id
                        ? 'justify-end'
                        : ''
                    }`}
                    key={i}
                  >
                    <div
                      className={`flex gap-2 ${
                        ch.isMe || ch.user?.id === user.id || ch?.user_id === user.id
                          ? 'order-2'
                          : 'order1'
                      }`}
                    >
                      <AvatarImage
                        size='xl'
                        source={ch.image ? ch.image : ch.user?.image}
                        extraClass={
                          ch.isMe || ch.user?.id === user.id || ch?.user_id === user.id
                            ? 'order-2'
                            : ''
                        }
                      />
                      <div
                        className={`chat__body relative min-w-[7rem] ${
                          ch.isMe || ch.user?.id === user.id || ch?.user_id === user.id
                            ? 'order-1 before:hidden cursor-pointer'
                            : 'after:hidden cursor-pointer'
                        } ${ch.isDeleted ? 'opacity-75' : ''} `}
                      >
                        <div
                          className={`flex gap-2 ${
                            ch.isMe || ch?.user?.id === user.id || ch?.user_id === user.id
                              ? 'justify-end'
                              : ''
                          }`}
                        >
                          <div className='flex flex-col'>
                            <p
                              className={`flex ${
                                ch.user_id === user.id ? 'justify-end' : 'justify-start'
                              } text-black font-bold`}
                            >
                              {ch.username ? ch.username : ch.user?.username}
                            </p>
                            {ch.user?.ogma_org?.length > 0 && (
                              <span className='text-xs text-gray-500'>
                                Org: {ch.user.ogma_org.map((org: any) => org.name).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>

                        <p className='text-black break-all border-t border-gray'>{ch.message}</p>
                        {Array.isArray(ch.attachments) && ch.attachments.length > 0 && (
                          <div className='mt-2 flex gap-2 flex-wrap'>
                            {ch.attachments.map((url: string, index: number) => {
                              const isImage = /\.(jpeg|jpg|gif|png|webp)$/i.test(url);
                              return (
                                // {isImage ? () : ()}
                                <div key={index} className=' overflow-hidden'>
                                  {isImage ? (
                                    <a href={url} target='_blank' rel='noopener noreferrer'>
                                      <img
                                        src={url}
                                        alt={`attachment-${index}`}
                                        className=' w-20 h-20 object-cover rounded border hover:scale-105 transition-transform'
                                      />
                                    </a>
                                  ) : (
                                    <div className='text-xs underline break-all'>
                                      {url.split('/').pop()}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div className='flex justify-end text-xs'>
                          <p>
                            {ch.time.length !== 8
                              ? dateAndTimeFormatter(ch?.time, 'MMMM D, h:mm A z')
                              : dateAndTimeFormatter(
                                  (() => {
                                    const [h, m, s] = ch.time.split(':').map(Number);
                                    const now = new Date();
                                    return new Date(
                                      now.getFullYear(),
                                      now.getMonth(),
                                      now.getDate(),
                                      h,
                                      m,
                                      s,
                                    ).toISOString();
                                  })(),
                                  'MMMM D, h:mm A z',
                                )}
                          </p>
                        </div>
                        {ch.isDeleted ? (
                          <div
                            className={`absolute z-50 bottom-1 ${
                              ch.isMe || ch.user?.id === user.id || ch?.user_id === user.id
                                ? ''
                                : ''
                            }`}
                          >
                            <div className='group'>
                              <AiOutlineStop className='text-red-500' />
                              <div
                                className={`hidden group-hover:block absolute top-[-50px] z-50 w-40 max-w-40 h-fit bg-black opacity-75 rounded p-1 ${
                                  ch.isMe || ch.user?.id === user.id || ch?.user_id === user.id
                                    ? 'left-0'
                                    : 'left-0'
                                }`}
                              >
                                <span className='overflow-ellipsis whitespace-normal text-white'>
                                  This message has been deleted
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : user.roles !== UserRoles.Admin &&
                  user.roles !== UserRoles.Ogma &&
                  ch.isDeleted !== 1 ? (
                  <div
                    className={`flex gap-2 w-full max-w-full relative ${
                      ch.isMe || ch.user?.id === user.id || ch?.user_id === user.id
                        ? 'justify-end'
                        : ''
                    }`}
                    key={i}
                  >
                    <AvatarImage
                      size='xl'
                      source={ch.image ? ch.image : ch.user?.image}
                      extraClass={
                        ch.isMe || ch.user?.id === user.id || ch?.user_id === user.id
                          ? 'order-2'
                          : ''
                      }
                    />
                    <div
                      className={`chat__body ${
                        ch.isMe || ch.user?.id === user.id || ch?.user_id === user.id
                          ? 'order-1 before:hidden cursor-pointer'
                          : 'after:hidden cursor-pointer'
                      } `}
                    >
                      <div
                        className={`flex gap-2 ${
                          ch.isMe || ch?.user?.id === user.id || ch?.user_id === user.id
                            ? 'justify-end'
                            : ''
                        }`}
                      >
                        {/* <span>{ch.team ? ch.team : ch.user?.team[x]?.teamName}</span> */}
                      </div>

                      <p className='text-black break-all border-t border-gray'>{ch.message} </p>
                      <div className='flex justify-end text-xs'>
                        <p>
                          {ch.time
                            ? dateAndTimeFormatter(ch?.time, 'MMMM D, h:mm A z')
                            : dateAndTimeFormatter(
                                (() => {
                                  const [h, m, s] = ch.clock.split(':').map(Number);
                                  const now = new Date();
                                  return new Date(
                                    now.getFullYear(),
                                    now.getMonth(),
                                    now.getDate(),
                                    h,
                                    m,
                                    s,
                                  ).toISOString();
                                })(),
                                'MMMM D, h:mm A z',
                              )}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </React.Fragment>
            ))}

            {isLoading ? (
              <div>Loading</div>
            ) : (
              <div ref={elementRef} className='flex flex-1 w-full min-h-[10px]'></div>
            )}
            {!isAtBottom && (
              <div className='border-2 border-black w-fit p-1 text-sm flex self-center items-center rounded absolute bottom-[200px] left-1/2 transform -translate-x-1/2'>
                More Messages
                <FaArrowDown className='ml-2' />
              </div>
            )}
            {!hasIOSupport && data.meta.next && <span>Load more</span>}
          </div>

          <div className='flex p-2  '>
            <div className='flex border-1 w-full items-center outline-2 p-2'>
              <input
                onChange={(e) => setTempChat(e.target.value)}
                type='text'
                placeholder='Start writing...'
                name=''
                id=''
                className='flex-1 active:outline-none focus:outline-none'
                value={tempChat}
              />

              <AiOutlineSend onClick={handleSubmit} />
            </div>
          </div>
          <div className='flex flex-col p-2  drag-upload'>
            <p className='text-red-700'>{errorMsg}</p>
            <DragDropFile file={file} setFile={setFile} extraClass='!h-[100px]' />
          </div>
        </form>
      </div>
      <div className='mt-4'>
        <Link
          to={user.roles === UserRoles.Ogma ? '../inbox' : '../'}
          className='border-2 border-grey-300 p-1 px-2 rounded'
        >
          Close
        </Link>
      </div>
      <Outlet />
    </>
  );
};

export default ViewChatRoom;
